import axios from "axios";
import { getValidToken } from "./getValidToken";
 
const { REACT_APP_API_BASE_URL = "https://esbdev.premier-research.com" } =
  process.env;
 
// ─── Request interceptor: attach token or abort ─────────────────────────────
axios.interceptors.request.use(
  async (config) => {
    const token = await getValidToken();
 
    if (!token) {
      // A redirect to Microsoft login is in progress.
      // Abort this request so it never hits the backend unauthenticated.
      const controller = new AbortController();
      controller.abort("Auth redirect in progress");
      config.signal = controller.signal;
      return config;
    }
 
    config.headers["Authorization"] = token;
    return config;
  },
  (error) => Promise.reject(error),
);
 
// ─── Response interceptor: handle 401 ───────────────────────────────────────
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Swallow aborted requests silently — these are intentional auth redirects
    if (axios.isCancel(error) || error?.code === "ERR_CANCELED") {
      // Reject with a cancel error so callers can handle it,
      // but mark it clearly as auth-related so no one pushes to /health
      return Promise.reject({ __authRedirect: true, code: "ERR_CANCELED" });
    }
 
    if (error.response?.status === 401) {
      sessionStorage.clear();
      localStorage.removeItem("user");
      // Next request will trigger getValidToken → loginRedirect
    }
 
    return Promise.reject(error);
  },
);
 
// ─────────────────────────────────────────────────────────────────────────────

export default {
  deleteStudyPlan: function (studyId, studyPlanId) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/EnrollmentPlanDelete`,
      { studyId, studyPlanId },
    );
  },
  updateRiskAssessment: function (studyId, riskAssessment) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/InitialRiskAssessment`,
      riskAssessment,
    );
  },
  updateVisitSampleStrategy: function (studyId, visitSampleStrategy) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/VisitSamplingStrategyNotes`,
      visitSampleStrategy,
    );
  },
  getRiskAssessments: function (scheduleOfEventsId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/soe/${scheduleOfEventsId}/riskassessments`,
    );
  },
  getVisitSampleStrategy: function (scheduleOfEventsId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/soe/${scheduleOfEventsId}/visitsamplingstrategy`,
    );
  },
  syncSubmissionDates: function (studyId, payload) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/MilestoneSyncSubmission`,
      payload,
    );
  },
  refreshSiteSelectedList: function (studyId, scheduleOfEventsId) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/SOESiteSelectedList`,
      { scheduleOfEventsId },
    );
  },
  sitesIncludedInRemodel: function (studyId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/remodel/study/${studyId}/sites/included`,
    );
  },
  sitesNotIncludedInRemodel: function (studyId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/remodel/study/${studyId}/sites/notincluded`,
    );
  },
  getUploadedFiles: function () {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/files`);
  },
  uploadProtocol: function (data) {
    return axios.post(`${REACT_APP_API_BASE_URL}/ctpo/files`, data);
  },
  health: function () {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/meta`);
  },
  getAssessmentNames: function (query = "") {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/assessments/name/${query}*`,
    );
  },
  getStudies: function (query) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/studies/project/${query}*`,
    );
  },
  loadScenarioList: function (projectId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/studies/${projectId}/plans`,
    );
  },
  getStudy: function (id) {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/plans/study/${id}`);
  },
  getSOEs: function (id) {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/soe/study/${id}`);
  },
  getSOE: function (id) {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/soe/${id}`);
  },
  getSOEMapping: function (id) {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/soe/${id}/mapping`);
  },
  getSOEStudyVisits: function (id) {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/soe/${id}/visits`);
  },
  getSOEStudyAssessments: function (id) {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/soe/${id}/assessments`);
  },
  getSOECountries: function (id) {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/soe/${id}/sitegroups`);
  },
  getSOECountryVisits: function (scheduleOfEventsId, siteGroupId, siteId = 1) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/soe/${scheduleOfEventsId}/visits/sitegroup/${siteGroupId}/site/${siteId}`,
    );
  },
  getSOECountryAssessments: function (scheduleOfEventsId, siteGroupId, siteId = 1) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/soe/${scheduleOfEventsId}/assessments/sitegroup/${siteGroupId}/site/${siteId}`,
    );
  },
  getSOEForecastDetail: function (scheduleOfEventsId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/soe/${scheduleOfEventsId}/detail`,
    );
  },
  getSOEForecastSummary: function (scheduleOfEventsId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/soe/${scheduleOfEventsId}/summary`,
    );
  },
  getSOEForecastRFPDetail: function (scheduleOfEventsId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/soe/${scheduleOfEventsId}/rfp/detail`,
    );
  },
  getSOEForecastRFPSummary: function (scheduleOfEventsId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/soe/${scheduleOfEventsId}/rfp/summary`,
    );
  },
  getStudyPlan: function (id) {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/plan/${id}`);
  },
  getCohortStudyPlan: function (id) {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/cohort/plan/${id}`);
  },
  updateCohortStudyPlan: function (id, data) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${id}/SequentialCohort`,
      data,
    );
  },
  createNewScenario: function (newScenario) {
    return axios.post(`${REACT_APP_API_BASE_URL}/ctpo/plans`, newScenario);
  },
  createNewPlan: function (plan) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${plan.studyId}/EnrollmentPlanStep1`,
      plan,
    );
  },
  createNewSOEClone: function (soe, studyId) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/SOEClone`,
      soe,
    );
  },
  deleteSOE: function (scheduleOfEventsId, studyId) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/SOEDelete`,
      { scheduleOfEventsId, studyId: parseInt(studyId) },
    );
  },
  createNewSOEPlan: function (soe) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${soe.studyId}/soe`,
      soe,
    );
  },
  updateSOE: function (updateSOE) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${updateSOE.studyId}/SOEEdit`,
      updateSOE,
    );
  },
  createSOEVisits: function (visits, studyId) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/SOEVisit`,
      visits,
    );
  },
  createSOEAssessments: function (assessments, studyId) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/SOEAssessment`,
      assessments,
    );
  },
  createSOECountryVisits: function (visits, studyId) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/SOEVisitSiteGroup`,
      visits,
    );
  },
  createSOECountryAssessments: function (assessments, studyId) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/SOEAssessmentSiteGroup`,
      assessments,
    );
  },
  createSOEAssessmentVisitMapping: function (mappings, studyId) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/SOEAssessmentVisitMapping`,
      mappings,
    );
  },
  createNewCohortPlan: function (combinedPlan, studyId) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/cohort/study/${studyId}`,
      combinedPlan,
    );
  },
  setSOESiteContractDate: function (input, studyId) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/SOESiteContractDate`,
      input,
    );
  },
  createNewClone: function (newClone) {
    return axios.post(`${REACT_APP_API_BASE_URL}/ctpo/clone`, newClone);
  },
  createNewVersion: function (newVersion) {
    return axios.post(`${REACT_APP_API_BASE_URL}/ctpo/clone`, newVersion);
  },
  startSimulation: function (studyPlanId) {
    return axios.post(`${REACT_APP_API_BASE_URL}/ctpo/start`, studyPlanId);
  },
  getSiteGroups: function () {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/sitegroups`);
  },
  getAdminSiteGroups: function () {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/admin/sitegroups`);
  },
  getSingleAdminSiteGroup: function (id) {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/admin/sitegroup/${id}`);
  },
  patchAdminSiteGroup: function (body) {
    return axios.patch(`${REACT_APP_API_BASE_URL}/ctpo/admin/sitegroup`, body);
  },
  postSiteGroup: function (body, studyId) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/EnrollmentPlanCountryAdd`,
      body,
    );
  },
  editSiteGroup: function (body, studyId) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/EnrollmentPlanCountryEdit`,
      body,
    );
  },
  getCountries: function () {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/countries`);
  },
  deleteCountry: function (body, studyId) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/EnrollmentPlanCountryDelete`,
      body,
    );
  },
  updateCountry: function (body) {
    return axios.patch(`${REACT_APP_API_BASE_URL}/ctpo/country`, body);
  },
  updatePlan: function (editedScenario, studyId) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/EnrollmentPlanStep1`,
      editedScenario,
    );
  },
  updatePlanStep2: function (obj) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${obj.studyId}/EnrollmentPlanStep2`,
      obj,
    );
  },
  updateStudyPlan: function (editedScenario, studyId) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/EnrollmentPlanSaveAs`,
      editedScenario,
    );
  },
  getMilestones: function () {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/milestones`);
  },
  getParameters: function () {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/parameters`);
  },
  signIn: async function () {
    return axios.post(`${REACT_APP_API_BASE_URL}/ctpo/login`, null);
  },
  getFavorites: function (email) {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/favorites/user/${email}`);
  },
  deleteFavorite: function (favorite) {
    return axios.delete(
      `${REACT_APP_API_BASE_URL}/ctpo/favorite?studyId=${favorite.studyId}&userId=${favorite.userId}`,
    );
  },
  postFavorite: function (favorite) {
    return axios.post(`${REACT_APP_API_BASE_URL}/ctpo/favorite`, favorite);
  },
  getMilestoneDates: function (body) {
    return axios.post(`${REACT_APP_API_BASE_URL}/ctpo/milestonedates`, body);
  },
  deleteActivationProfiles: function (id) {
    return axios.delete(
      `${REACT_APP_API_BASE_URL}/ctpo/activationprofile/${id}`,
    );
  },
  createActivationProfiles: function (body) {
    return axios.post(`${REACT_APP_API_BASE_URL}/ctpo/activationprofile`, body);
  },
  editActivationProfiles: function (body) {
    return axios.put(`${REACT_APP_API_BASE_URL}/ctpo/activationprofile`, body);
  },
  editEnrollmentVariation: function (body) {
    return axios.put(
      `${REACT_APP_API_BASE_URL}/ctpo/enrollmentvariation`,
      body,
    );
  },
  runSimulation: function (body) {
    return axios.post(`${REACT_APP_API_BASE_URL}/ctpo/runsimulation`, body);
  },
  checkSimulationStatus: function (planId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/simulation/status/plan/${planId}`,
    );
  },
  getSimulationResults: function (planId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/simulation/run/plan/${planId}`,
    );
  },
  getSimulationDetailTableByMonth: function (planId, type = "results") {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/${type}/Monthly (Cumulative)/plan/${planId}`,
    );
  },
  getSimulationDetailTableByWeek: function (planId, type = "results") {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/${type}/Weekly (Cumulative)/plan/${planId}`,
    );
  },
  getSimulationDetailTableByMonthNonCumulative: function (planId, type = "results") {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/${type}/Monthly (Non-Cumulative)/plan/${planId}`,
    );
  },
  getSimulationDetailTableByWeekNonCumulative: function (planId, type = "results") {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/${type}/Weekly (Non-Cumulative)/plan/${planId}`,
    );
  },
  getSimulationSummaryTable: function (planId, studyType = "results") {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/${studyType}/summary/plan/${planId}`,
    );
  },
  getSimulationEnrollmentTable: function (planId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/results/enrollment/plan/${planId}`,
    );
  },
  getSimulationStartupTable: function (planId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/results/startup/plan/${planId}`,
    );
  },
  getSimulationDurationTable: function (planId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/results/duration/plan/${planId}`,
    );
  },
  getSimulationTimelineTable: function (planId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/results/timeline/plan/${planId}`,
    );
  },
  getOverallChart: function (planId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/charts/month/plan/${planId}`,
    );
  },
  getCountryChart: function (planId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/charts/week/plan/${planId}`,
    );
  },
  getRegionChart: function (planId, dataSet = "MONTH") {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/regioncharts/${dataSet}/plan/${planId}`,
    );
  },
  getSiteGroupChart: function (planId, dataSet = "WEEK") {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/charts/${dataSet}/plan/${planId}`,
    );
  },
  getCohortChartMonthly: function (planId, dataSet = "MONTH") {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/cohortcharts/${dataSet}/plan/${planId}`,
    );
  },
  getCohortChartWeekly: function (planId, dataSet = "WEEK") {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/cohortcharts/${dataSet}/plan/${planId}`,
    );
  },
  getCohortTimelineChart: function (planId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/cohortcharts/timeline/plan/${planId}`,
    );
  },
  getStudyActuals: function (studyId) {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/actuals/study/${studyId}`);
  },
  loadStudyActuals: function (studyId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/loadactuals/study/${studyId}`,
    );
  },
  getStudyRemodel: function (studyId) {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/remodel/study/${studyId}`);
  },
  updateStudyRemodel: function (studyId, body) {
    return axios.patch(
      `${REACT_APP_API_BASE_URL}/ctpo/remodel/study/${studyId}`,
      body,
    );
  },
  uploadPlan: function (studyId, data) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/EnrollmentPlanUpload`,
      data,
    );
  },
  uploadCost: function (studyId, data) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/SOEUploadCountry`,
      data,
    );
  },
  uploadContract: function (studyId, data) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/SOEUploadSite`,
      data,
    );
  },
  getCoutryCosts: function (scheduleOfEventsId, siteGroupId) {
    return axios.get(
      `${REACT_APP_API_BASE_URL}/ctpo/soe/${scheduleOfEventsId}/defaultcost/sitegroup/${siteGroupId}`,
    );
  },
  uploadCountryCosts: function (studyId, data) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/AssessmentDefaultCost`,
      data,
    );
  },
  uploadAdminData: function (studyId, data) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/AdminData`,
      data,
    );
  },
  updateAdminData: function (data) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/0/AdminDataUpload`,
      data,
    );
  },
  saveUserLog: function (data) {
    return axios.post(`${REACT_APP_API_BASE_URL}/ctpo/study/0/UserLogin`, data);
  },
  saveSOECurrencyData: function (studyId, data) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/SOECurrency`,
      data,
    );
  },
  getQuickLinks: function () {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/quickLinks`);
  },
  getCurrencies: function () {
    return axios.get(`${REACT_APP_API_BASE_URL}/ctpo/currencies`);
  },
  createSOEAssessmentVisitMappingUpload: function (mappings, studyId) {
    return axios.post(
      `${REACT_APP_API_BASE_URL}/ctpo/study/${studyId}/SOEAssessmentVisitMappingUpload`,
      mappings,
    );
  },
};